import React, { createContext, useContext, useEffect, useState } from "react";
import PermissionApi, { TestRequestPermissions } from "../api/services/PermissionApi";
import ErrorSnackbarContext from "./ErrorSnackbarContext";
import CustomLoading from "../components/Alert/CustomLoading";
import useEnv from "../hooks/env";
import TestRequestPurpose from "../api/models/test-request/TestRequestPurpose";

interface GenericPermissionContextType {
  accessAdmin: boolean;
  accessProducts: boolean;
  createAProERequest: boolean;
  createTestRequest: boolean;
  createHistoricalTestRequest: boolean;
  editUserGroups: boolean;
  editLaboratoryName: boolean;
  editUserLaboratories: boolean;
  editLaboratoryManagers: boolean;
  exportArchivedSearch: boolean;
  exportProjectData: boolean;
  extendedAdminRights: boolean;
  viewLaboratoryTab: boolean;
  listOtherTestRequests: boolean;
  newTestRequest: TestRequestPermissions | null;
  newTestRequests: Record<TestRequestPurpose, TestRequestPermissions | null>;
  viewAdditionalStatistics: boolean;
  viewUserTable: boolean;
  viewReportTemplatesTab: boolean;
}

const emptyNewTestRequests = {
  DEFAULT: null,
  APPLICATION_PROCESS: null,
};

export const GenericPermissionContext = createContext<GenericPermissionContextType>({
  accessAdmin: false,
  accessProducts: false,
  createAProERequest: false,
  createTestRequest: false,
  createHistoricalTestRequest: false,
  editUserGroups: false,
  editLaboratoryName: false,
  editUserLaboratories: false,
  editLaboratoryManagers: false,
  exportArchivedSearch: false,
  exportProjectData: false,
  extendedAdminRights: false,
  viewLaboratoryTab: false,
  listOtherTestRequests: false,
  newTestRequest: null,
  newTestRequests: emptyNewTestRequests,
  viewAdditionalStatistics: false,
  viewUserTable: false,
  viewReportTemplatesTab: false,
});

export const GenericPermissionContextProvider: React.FunctionComponent = (
  props: React.PropsWithChildren<Record<string, unknown>>,
) => {
  const { api } = useEnv();
  const permissionApi = new PermissionApi(api);
  const [accessAdmin, setAccessAdmin] = useState(false);
  const [accessProducts, setAccessProducts] = useState(false);
  const [createAProERequest, setCreateAProERequest] = useState(false);
  const [createTestRequest, setCreateTestRequest] = useState(false);
  const [createHistoricalTestRequest, setCreateHistoricalTestRequest] = useState(false);
  const [editUserGroups, setEditUserGroups] = useState(false);
  const [editLaboratoryName, setEditLaboratoryName] = useState(false);
  const [editUserLaboratories, setEditUserLaboratories] = useState(false);
  const [editLaboratoryManagers, setEditLaboratoryManagers] = useState(false);
  const [exportArchivedSearch, setExportArchivedSearch] = useState(false);
  const [exportProjectData, setExportProjectData] = useState(false);
  const [extendedAdminRights, setExtendedAdminRights] = useState(false);
  const [viewLaboratoryTab, setViewLaboratoryTab] = useState(false);
  const [viewReportTemplatesTab, setViewReportTemplatesTab] = useState(false);
  const [listOtherTestRequestInDraft, setListOtherTestRequestInDraft] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [newTestRequest, setNewTestRequest] = useState<TestRequestPermissions | null>(null);
  const [newTestRequests, setNewTestRequests] =
    useState<Record<TestRequestPurpose, TestRequestPermissions | null>>(emptyNewTestRequests);
  const [viewAdditionalStatistics, setViewAdditionalStatistics] = useState(false);
  const [viewUserTable, setViewUserTable] = useState(false);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { children } = props;

  useEffect(() => {
    permissionApi
      .fetchGenericPermissions()
      .then((response) => {
        setAccessAdmin(response.data.accessAdmin || false);
        setAccessProducts(response.data.accessProducts || false);
        setCreateAProERequest(response.data.createAProERequest || false);
        setCreateTestRequest(response.data.createTestRequest || false);
        setCreateHistoricalTestRequest(response.data.createHistoricalTestRequest || false);
        setEditUserGroups(response.data.editUserGroups || false);
        setEditLaboratoryName(response.data.editLaboratoryName || false);
        setEditUserLaboratories(response.data.editUserLaboratories || false);
        setEditLaboratoryManagers(response.data.editLaboratoryManagers || false);
        setExportArchivedSearch(response.data.exportArchivedSearch || false);
        setExportProjectData(response.data.exportProjectData || false);
        setExtendedAdminRights(response.data.extendedAdminRights || false);
        setViewLaboratoryTab(response.data.viewLaboratoryTab || false);
        setListOtherTestRequestInDraft(response.data.listOtherTestRequests || false);
        setViewReportTemplatesTab(response.data.viewReportTemplatesTab || false);
        setNewTestRequest(response.data.newTestRequest || null);
        setNewTestRequests(
          (response.data.newTestRequests as Record<TestRequestPurpose, TestRequestPermissions | null>) ||
            emptyNewTestRequests,
        );
        setViewAdditionalStatistics(response.data.viewAdditionalStatistics || false);
        setViewUserTable(response.data.viewUserTable || false);
        setLoaded(true);
      })
      .catch(handleHttpError);
  }, []);

  return (
    <GenericPermissionContext.Provider
      value={{
        accessAdmin,
        accessProducts,
        createAProERequest,
        createTestRequest,
        createHistoricalTestRequest,
        editUserGroups,
        editLaboratoryName,
        editUserLaboratories,
        editLaboratoryManagers,
        exportArchivedSearch,
        exportProjectData,
        extendedAdminRights,
        viewLaboratoryTab,
        listOtherTestRequests: listOtherTestRequestInDraft,
        newTestRequest,
        newTestRequests,
        viewAdditionalStatistics,
        viewUserTable,
        viewReportTemplatesTab,
      }}
    >
      {!loaded && <CustomLoading />}
      {loaded && children}
    </GenericPermissionContext.Provider>
  );
};

export default GenericPermissionContext;
